import { readFile, stat } from "node:fs/promises";
import path from "node:path";
import { z } from "zod";

export const PRIMEUI_PROJECT_DIR_NAME = ".primeui";
export const PRIMEUI_PROJECT_CONFIG_RELATIVE_PATH = `${PRIMEUI_PROJECT_DIR_NAME}/project.json`;
export type PrimeUiProjectConfigSource = "tool" | "sticky" | "env" | "search";

export type PrimeUiProjectConfigErrorCode =
  | "PROJECT_CONFIG_NOT_FOUND"
  | "PROJECT_CONFIG_READ_FAILED"
  | "PROJECT_CONFIG_INVALID_JSON"
  | "PROJECT_CONFIG_INVALID_FORMAT"
  | "PROJECT_API_KEY_MISSING";

type PrimeUiProjectConfigErrorOptions = {
  code: PrimeUiProjectConfigErrorCode;
  message: string;
  hint?: string;
  details?: Record<string, unknown>;
};

export class PrimeUiProjectConfigError extends Error {
  readonly code: PrimeUiProjectConfigErrorCode;
  readonly hint?: string;
  readonly details: Record<string, unknown>;

  constructor(options: PrimeUiProjectConfigErrorOptions) {
    super(options.message);
    this.name = "PrimeUiProjectConfigError";
    this.code = options.code;
    this.hint = options.hint;
    this.details = options.details ?? {};
  }
}

function buildProjectRootHint(): string {
  return "Likely MCP project-path bug: when Codex VSCode runs MCP from global config, server can start in HOME without project context. Fix: set tool input parameter `projectRoot` to the ABSOLUTE path of the current project root (for example: /path/to/current/project).";
}

const primeUiProjectConfigSchema = z
  .object({
    projectId: z.string().trim().min(1),
    apiKey: z.string().trim().min(1),
    apiBaseUrl: z.string().trim().url().optional(),
    targetProjectPath: z
      .string()
      .trim()
      .regex(/^\.\/.*$/)
      .optional(),
  })
  .passthrough();

const primeUiProjectConfigWithTargetPathSchema =
  primeUiProjectConfigSchema.superRefine((value, ctx) => {
    if (!value.targetProjectPath) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["targetProjectPath"],
        message: "Required",
      });
    }
  });

export type PrimeUiProjectConfig = z.infer<typeof primeUiProjectConfigSchema>;

export type ResolvedPrimeUiProjectConfig = {
  projectRoot: string;
  projectConfigPath: string;
  projectConfig: PrimeUiProjectConfig;
  source: PrimeUiProjectConfigSource;
};

export type ResolvedPrimeUiApiKey = {
  apiKey?: string;
  source: "env" | "config" | "missing";
  envProvided: boolean;
  configProvided: boolean;
  valuesMatch?: boolean;
};

async function fileExists(filePath: string): Promise<boolean> {
  try {
    const fileStats = await stat(filePath);
    return fileStats.isFile();
  } catch {
    return false;
  }
}

function ancestors(startPath: string): string[] {
  const resolvedStartPath = path.resolve(startPath);
  const directories: string[] = [];
  let currentPath = resolvedStartPath;

  while (true) {
    directories.push(currentPath);
    const parentPath = path.dirname(currentPath);
    if (parentPath === currentPath) {
      break;
    }
    currentPath = parentPath;
  }

  return directories;
}

export async function findPrimeUiProjectConfigPath(
  startPath: string,
): Promise<string | undefined> {
  for (const currentPath of ancestors(startPath)) {
    const candidatePath = path.join(
      currentPath,
      PRIMEUI_PROJECT_CONFIG_RELATIVE_PATH,
    );
    if (await fileExists(candidatePath)) {
      return candidatePath;
    }
  }
  return undefined;
}

export async function readPrimeUiProjectConfig(
  projectConfigPath: string,
  options: {
    requireTargetProjectPath?: boolean;
  } = {
    requireTargetProjectPath: true,
  },
): Promise<PrimeUiProjectConfig> {
  const requireTargetProjectPath = options.requireTargetProjectPath !== false;

  let projectConfigRaw: string;
  try {
    projectConfigRaw = await readFile(projectConfigPath, "utf-8");
  } catch (error) {
    throw new PrimeUiProjectConfigError({
      code: "PROJECT_CONFIG_READ_FAILED",
      message: `[primeui-mcp] failed to read ${PRIMEUI_PROJECT_CONFIG_RELATIVE_PATH}: ${
        error instanceof Error ? error.message : String(error)
      }`,
      details: {
        projectConfigPath,
      },
      hint: buildProjectRootHint(),
    });
  }

  let projectConfigJson: unknown;
  try {
    projectConfigJson = JSON.parse(projectConfigRaw);
  } catch (error) {
    throw new PrimeUiProjectConfigError({
      code: "PROJECT_CONFIG_INVALID_JSON",
      message: `[primeui-mcp] invalid JSON in ${PRIMEUI_PROJECT_CONFIG_RELATIVE_PATH}: ${
        error instanceof Error ? error.message : String(error)
      }`,
      details: {
        projectConfigPath,
      },
      hint: buildProjectRootHint(),
    });
  }

  const parsedConfig = (
    requireTargetProjectPath
      ? primeUiProjectConfigWithTargetPathSchema
      : primeUiProjectConfigSchema
  ).safeParse(projectConfigJson);
  if (!parsedConfig.success) {
    const details = parsedConfig.error.issues
      .map((issue) => `${issue.path.join(".") || "<root>"}: ${issue.message}`)
      .join("; ");
    throw new PrimeUiProjectConfigError({
      code: "PROJECT_CONFIG_INVALID_FORMAT",
      message: `[primeui-mcp] invalid ${PRIMEUI_PROJECT_CONFIG_RELATIVE_PATH} format: ${details}`,
      details: {
        projectConfigPath,
      },
      hint: buildProjectRootHint(),
    });
  }

  return parsedConfig.data;
}

function withConfigSourceDetails(
  error: unknown,
  context: {
    source: PrimeUiProjectConfigSource;
    projectRoot: string;
    projectConfigPath: string;
  },
): never {
  if (!(error instanceof PrimeUiProjectConfigError)) {
    throw error;
  }

  throw new PrimeUiProjectConfigError({
    code: error.code,
    message: error.message,
    hint: error.hint,
    details: {
      ...error.details,
      source: context.source,
      projectRoot: context.projectRoot,
      expectedConfigPath: context.projectConfigPath,
    },
  });
}

async function loadResolvedPrimeUiProjectConfig(
  context: {
    source: PrimeUiProjectConfigSource;
    projectRoot: string;
    projectConfigPath: string;
  },
  options: {
    requireTargetProjectPath?: boolean;
  },
): Promise<ResolvedPrimeUiProjectConfig> {
  try {
    const projectConfig = await readPrimeUiProjectConfig(
      context.projectConfigPath,
      {
        requireTargetProjectPath: options.requireTargetProjectPath,
      },
    );
    return {
      projectRoot: context.projectRoot,
      projectConfigPath: context.projectConfigPath,
      projectConfig,
      source: context.source,
    };
  } catch (error) {
    withConfigSourceDetails(error, context);
  }
}

type ResolvePrimeUiProjectConfigOptions = {
  cwd: string;
  projectRootFromTool?: string;
  projectRootFromSticky?: string;
  projectRootFromEnv?: string;
  fallbackCwds?: string[];
};

function toUniqueResolvedDirs(
  directories: Array<string | undefined>,
): string[] {
  const resolved = new Set<string>();

  for (const directory of directories) {
    const trimmed = directory?.trim();
    if (!trimmed) {
      continue;
    }
    resolved.add(path.resolve(trimmed));
  }

  return [...resolved];
}

export function buildPrimeUiProjectSearchRoots(options: {
  cwd: string;
  fallbackCwds?: string[];
}): string[] {
  return toUniqueResolvedDirs([...(options.fallbackCwds ?? []), options.cwd]);
}

export async function resolvePrimeUiProjectConfig(
  options: ResolvePrimeUiProjectConfigOptions,
  configOptions: {
    requireTargetProjectPath?: boolean;
  } = {
    requireTargetProjectPath: true,
  },
): Promise<ResolvedPrimeUiProjectConfig> {
  const toolProjectRoot = options.projectRootFromTool?.trim();
  if (toolProjectRoot) {
    const resolvedToolRoot = path.resolve(toolProjectRoot);
    const toolProjectConfigPath = path.join(
      resolvedToolRoot,
      PRIMEUI_PROJECT_CONFIG_RELATIVE_PATH,
    );
    if (!(await fileExists(toolProjectConfigPath))) {
      throw new PrimeUiProjectConfigError({
        code: "PROJECT_CONFIG_NOT_FOUND",
        message: `[primeui-mcp] ${PRIMEUI_PROJECT_CONFIG_RELATIVE_PATH} not found in provided projectRoot (${resolvedToolRoot}).`,
        details: {
          source: "tool",
          projectRoot: resolvedToolRoot,
          expectedConfigPath: toolProjectConfigPath,
          cwd: options.cwd,
        },
        hint: buildProjectRootHint(),
      });
    }

    return loadResolvedPrimeUiProjectConfig(
      {
        source: "tool",
        projectRoot: resolvedToolRoot,
        projectConfigPath: toolProjectConfigPath,
      },
      configOptions,
    );
  }

  const stickyProjectRoot = options.projectRootFromSticky?.trim();
  if (stickyProjectRoot) {
    const resolvedStickyRoot = path.resolve(stickyProjectRoot);
    const stickyProjectConfigPath = path.join(
      resolvedStickyRoot,
      PRIMEUI_PROJECT_CONFIG_RELATIVE_PATH,
    );
    if (await fileExists(stickyProjectConfigPath)) {
      return loadResolvedPrimeUiProjectConfig(
        {
          source: "sticky",
          projectRoot: resolvedStickyRoot,
          projectConfigPath: stickyProjectConfigPath,
        },
        configOptions,
      );
    }
  }

  const envProjectRoot = options.projectRootFromEnv?.trim();
  if (envProjectRoot) {
    const resolvedEnvRoot = path.resolve(envProjectRoot);
    const envProjectConfigPath = path.join(
      resolvedEnvRoot,
      PRIMEUI_PROJECT_CONFIG_RELATIVE_PATH,
    );
    if (!(await fileExists(envProjectConfigPath))) {
      throw new PrimeUiProjectConfigError({
        code: "PROJECT_CONFIG_NOT_FOUND",
        message: `[primeui-mcp] ${PRIMEUI_PROJECT_CONFIG_RELATIVE_PATH} not found in PRIMEUI_PROJECT_ROOT (${resolvedEnvRoot}).`,
        details: {
          source: "env",
          envVar: "PRIMEUI_PROJECT_ROOT",
          projectRoot: resolvedEnvRoot,
          expectedConfigPath: envProjectConfigPath,
          cwd: options.cwd,
        },
        hint: buildProjectRootHint(),
      });
    }

    return loadResolvedPrimeUiProjectConfig(
      {
        source: "env",
        projectRoot: resolvedEnvRoot,
        projectConfigPath: envProjectConfigPath,
      },
      configOptions,
    );
  }

  const searchRoots = buildPrimeUiProjectSearchRoots({
    cwd: options.cwd,
    fallbackCwds: options.fallbackCwds,
  });

  let projectConfigPath: string | undefined;
  for (const searchRoot of searchRoots) {
    const foundProjectConfigPath =
      await findPrimeUiProjectConfigPath(searchRoot);
    if (foundProjectConfigPath) {
      projectConfigPath = foundProjectConfigPath;
      break;
    }
  }

  if (!projectConfigPath) {
    throw new PrimeUiProjectConfigError({
      code: "PROJECT_CONFIG_NOT_FOUND",
      message: `[primeui-mcp] ${PRIMEUI_PROJECT_CONFIG_RELATIVE_PATH} not found. MCP cwd is "${options.cwd}". Searched from: ${searchRoots.join(
        ", ",
      )}. This usually means MCP did not receive the current project path.`,
      details: {
        source: "search",
        cwd: options.cwd,
        searchedRoots: searchRoots,
      },
      hint: buildProjectRootHint(),
    });
  }

  const projectRoot = path.dirname(path.dirname(projectConfigPath));
  return loadResolvedPrimeUiProjectConfig(
    {
      source: "search",
      projectRoot,
      projectConfigPath,
    },
    configOptions,
  );
}

type ResolvePrimeUiApiKeyOptions = {
  projectConfig?: PrimeUiProjectConfig;
  apiKeyFromEnv?: string;
};

export type ResolvedPrimeUiApiBaseUrl = {
  baseUrl?: string;
  source: "env" | "config" | "default";
  envProvided: boolean;
  configProvided: boolean;
  valuesMatch?: boolean;
};

export function resolvePrimeUiApiKeyDetails(
  options: ResolvePrimeUiApiKeyOptions,
): ResolvedPrimeUiApiKey {
  const envApiKey = options.apiKeyFromEnv?.trim();
  const configApiKey = options.projectConfig?.apiKey.trim();
  const envProvided = Boolean(envApiKey);
  const configProvided = Boolean(configApiKey);

  return {
    apiKey: envApiKey ?? configApiKey,
    source: envProvided ? "env" : configProvided ? "config" : "missing",
    envProvided,
    configProvided,
    valuesMatch:
      envProvided && configProvided ? envApiKey === configApiKey : undefined,
  };
}

export function resolvePrimeUiTargetProjectRoot(
  projectRoot: string,
  projectConfig: PrimeUiProjectConfig,
): string {
  if (!projectConfig.targetProjectPath) {
    throw new PrimeUiProjectConfigError({
      code: "PROJECT_CONFIG_INVALID_FORMAT",
      message: `[primeui-mcp] invalid ${PRIMEUI_PROJECT_CONFIG_RELATIVE_PATH} format: targetProjectPath: Required`,
      details: {
        projectRoot,
      },
      hint: buildProjectRootHint(),
    });
  }

  return path.resolve(projectRoot, projectConfig.targetProjectPath);
}

export async function resolvePrimeUiApiKey(
  options: ResolvePrimeUiApiKeyOptions,
): Promise<string> {
  const resolved = resolvePrimeUiApiKeyDetails(options);
  if (resolved.apiKey) {
    return resolved.apiKey;
  }

  throw new PrimeUiProjectConfigError({
    code: "PROJECT_API_KEY_MISSING",
    message:
      "[primeui-mcp] PRIMEUI_API_KEY is missing in env and .primeui/project.json",
    hint: "Set PRIMEUI_API_KEY or ensure apiKey is present in .primeui/project.json.",
  });
}

export function resolvePrimeUiApiBaseUrlDetails(options: {
  projectConfig?: PrimeUiProjectConfig;
  baseUrlFromEnv?: string;
}): ResolvedPrimeUiApiBaseUrl {
  const envBaseUrl = options.baseUrlFromEnv?.trim();
  const configBaseUrl = options.projectConfig?.apiBaseUrl?.trim();
  const envProvided = Boolean(envBaseUrl);
  const configProvided = Boolean(configBaseUrl);
  const resolvedBaseUrl = envBaseUrl || configBaseUrl;

  return {
    baseUrl: resolvedBaseUrl,
    source: envProvided ? "env" : configProvided ? "config" : "default",
    envProvided,
    configProvided,
    valuesMatch:
      envProvided && configProvided ? envBaseUrl === configBaseUrl : undefined,
  };
}

export function resolvePrimeUiApiBaseUrl(options: {
  projectConfig?: PrimeUiProjectConfig;
  baseUrlFromEnv?: string;
}): string | undefined {
  return resolvePrimeUiApiBaseUrlDetails(options).baseUrl;
}
